import { useMemo } from "react";
import type { ConversationMessageApiModel, UsedSource } from "@noa/api-core";
import { createReferenceMap, parseMessageWithReferences, type ReferenceMapValue } from "../../message-references";

interface UseMessageReferenceDocsResult {
  documents: UsedSource[];
  referenceMap: Map<string, ReferenceMapValue>;
  hasDocuments: boolean;
}

type TopSourceByDocument = Map<string, UsedSource>;

export const useMessageReferenceDocs = (
  chatMessage: ConversationMessageApiModel,
  isAssistant: boolean
): UseMessageReferenceDocsResult => {
  const documents = useMemo(() => {
    const topSourceByDocument = chatMessage.used_sources.reduce<TopSourceByDocument>((map, source) => {
      const existing = map.get(source.source_document_name);
      if (!existing || source.precedence_rank < existing.precedence_rank) {
        map.set(source.source_document_name, source);
      }
      return map;
    }, new Map());

    return [...topSourceByDocument.values()].sort((a, b) => a.precedence_rank - b.precedence_rank);
  }, [chatMessage.used_sources]);

  const referenceMap = useMemo(() => {
    const { references } = parseMessageWithReferences(chatMessage.content);

    return createReferenceMap({ references, chatMessage });
  }, [chatMessage]);

  return {
    documents,
    referenceMap,
    hasDocuments: isAssistant && chatMessage.used_sources.length > 0,
  };
};
